import {
  AfterViewInit, Directive,
  ElementRef, EventEmitter,
  forwardRef, Input,
  OnChanges, OnDestroy,
  OnInit, Output, Renderer2,
  SimpleChanges,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
// @ts-ignore
import AutoNumeric from 'autonumeric';
import {AutonumericDefaults} from '@angularfy/autonumeric';
export const AUTONUMERIC_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AutonumericDirective),
  multi: true
};

export type AutonumericUpdateTriggerEventType = 'raw' | 'change';

@Directive({
  selector: '[appAutonumeric]',
  providers: [AUTONUMERIC_CONTROL_VALUE_ACCESSOR],
  // tslint:disable-next-line:use-host-property-decorator no-host-metadata-property
  host: { '(blur)': 'onTouchedFn()' },
})
export class AutonumericDirective implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  /* Defines the strategy to apply when options change.
   * reset will drop any previous options
   * update will change old values one by one but keep those that are not mentioned in the new options
   */
  @Input() strategy: 'reset' | 'update' = 'reset';

  @Input() options: any;
  @Input() predefined: string;

  instance: AutoNumeric;
  private isDisabled = false;
  // tslint:disable-next-line:no-unused-variable
  private initialValue: any;
  unsubscribeFormat: () => void;
  unsubscribeRawValueModified: () => void;
  @Output()
  formatted = new EventEmitter();
  @Output()
  rawValueModified = new EventEmitter();
  onChangeFn: (value: any) => void = () => {};
  @Input() autonumericUpdateTriggerEventType: AutonumericUpdateTriggerEventType = 'raw';

  onTouchedFn = () => {};

  constructor(
    private elm: ElementRef,
    private defaults: AutonumericDefaults,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.instance = new AutoNumeric(
      this.elm.nativeElement,
      this.getOptions() as any
    );
  }

  private normalize(options: any) {
    const normalized = {};
    Object.keys((AutoNumeric as any).options).forEach(key => {
      if (typeof options[key] === 'undefined') {
        normalized[key] = (this.defaults as any)[key];
      } else {
        normalized[key] = options[key];
      }
    });
    return normalized;
  }

  ngAfterViewInit(): void {
    this.setDisabledState(this.isDisabled);
    this.unsubscribeFormat = this.renderer.listen(this.elm.nativeElement, 'autoNumeric:formatted', ($event) => {
      this.formatted.emit($event);
    });
    this.unsubscribeRawValueModified = this.renderer.listen(this.elm.nativeElement, 'autoNumeric:rawValueModified', ($event) => {
      if(this.autonumericUpdateTriggerEventType === 'raw') {
        this.onChangeFn($event.detail.newRawValue);
      }
      this.rawValueModified.emit($event);
    });
    this.unsubscribeRawValueModified = this.renderer.listen(this.elm.nativeElement, 'change', ($event) => {
      if(this.autonumericUpdateTriggerEventType === 'change') {
        this.onChangeFn(this.instance.getFormatted());
      }
    });
}

  private getOptions() {
    if (this.options === undefined && this.predefined === undefined) {
      return this.defaults;
    }
    if (this.options !== undefined && this.predefined !== undefined) {
      throw new Error('predefined attribute could not be combined with options. Please use either predefined or options');
    }
    if (this.options !== undefined) {
      return this.normalize(this.options);
    }
    const predefined = AutoNumeric.getPredefinedOptions()[this.predefined];
    return this.normalize(predefined);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.instance) {
      return;
    }
    if (changes.options || changes.predefined) {
      if (this.strategy === 'reset') {
        (this.instance as any).options.reset();
      }
      this.instance.update(this.getOptions());
    }
  }

  ngOnDestroy(): void {
    if(this.unsubscribeFormat){
      this.unsubscribeFormat();
    }
    if(this.unsubscribeRawValueModified) {
      this.unsubscribeRawValueModified();
    }
    try {
      this.instance.remove(); // remove listeners
    } catch (e) {
    }
  }

  writeValue(value: any): void {
    if (this.instance) {
      this.instance.set(value);
    } else {
      // autonumeric hasn't been initialised yet, store the value for later use
      this.initialValue = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.instance) {
      if (this.isDisabled) {
        this.renderer.setProperty((this.instance as any).domElement, 'disabled', 'disabled');
      } else {
        this.renderer.removeAttribute((this.instance as any).domElement, 'disabled');
      }
    }
  }
}
